/* eslint-disable react-hooks/exhaustive-deps */
import { ErrorMessage, Field, FieldArray } from 'formik';
import { Button, Card, Col, Form, FormControl, Row } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import Editor from '../../../../../components/editor/Editor';
import ImageWithErrorHandling from '../../../../../components/image/ImageWithErrorHandling';
import { ImageSelect } from '../../../../../components/inputs/ImageSelect';
import { getPartition } from '../../../../../utils/utils';
import { StepProps } from './AddEditNewsItem';

export const NewsItemForm = ({ touched, errors, values, setFieldValue, readOnly }: StepProps) => {
  const imageRequired = !values?.image_file_name;

  return (
    <>
      <Row>
        <Col xs={12}>
          <FormStepHeader title="News Item Details" showRequiredLegend={!readOnly} />
        </Col>
        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="name"
          >
            <FormLabel required>Name</FormLabel>
            <Field
              name="name"
              as={Form.Control}
              isInvalid={touched.name && !!errors.name}
              aria-required
            />
            <ErrorMessage name="name">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="details"
          >
            <FormLabel required>Details</FormLabel>
            <Field
              name="details"
              component={Editor}
              isInvalid={touched.details && !!errors.details}
              aria-required
            />
            <ErrorMessage name="details">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="image"
          >
            <ImageSelect
              label={`Image${values?.image_file_name ? ` (${values?.image_file_name})` : ''}`}
              name="image"
              onChange={setFieldValue}
              required={imageRequired}
            />
            <ImageWithErrorHandling
              src={`${values?.s3_base_url}/${getPartition(values?.id)}/thumb/${values?.image_file_name}`}
              alt="Error loading image"
            />
            <ErrorMessage name="image">
              {(msg) => (
                <div className="text-danger small mt-1">
                  {typeof msg === 'string' ? msg : JSON.stringify(msg)}
                </div>
              )}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="published_on"
          >
            <FormLabel required>Published On</FormLabel>
            <Field
              name="published_on"
              type="date"
              as={Form.Control}
              isInvalid={touched.published_on && !!errors.published_on}
              aria-required
            />
            <ErrorMessage name="published_on">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
        </Col>
        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <FieldArray
            name="news_item_links"
            render={(arrayHelpers) => (
              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="news_item_links"
              >
                <FormLabel className="h4">Links</FormLabel>
                <br />
                <div className="multi-content-scroll mt-2">
                  <Row>
                    {values?.news_item_links && values?.news_item_links.length > 0 ? (
                      values.news_item_links.map((link, index) => {
                        let meta = {
                          touched: Array.isArray(touched.news_item_links)
                            ? touched.news_item_links[index]
                            : {},
                          error: Array.isArray(errors.news_item_links)
                            ? (errors.news_item_links[index] ?? {})
                            : {}
                        };
                        return (
                          <Col
                            key={index}
                            xs={12}
                            className="mb-2"
                          >
                            <Card border="primary">
                              {typeof errors.news_item_links === 'string' ? (
                                <div>{errors.news_item_links}</div>
                              ) : null}

                              <Card.Body>
                                <Col className="ms-1">
                                  <Row>
                                    <Col
                                      xs={12}
                                      className="pe-1"
                                    >
                                      <Form.Group
                                        className="mb-lg-3 mb-1"
                                        controlId={`news_item_links.${index}.text`}
                                      >
                                        <FormLabel>Text</FormLabel>
                                        <Field
                                          name={`news_item_links.${index}.text`}
                                          as={Form.Control}
                                          isInvalid={
                                            meta.touched?.text &&
                                            !!(typeof meta.error === 'string'
                                              ? meta.error
                                              : meta.error.text)
                                          }
                                        />
                                        <ErrorMessage name={`news_item_links.${index}.text`}>
                                          {(msg) => (
                                            <FormControl.Feedback type="invalid">
                                              {msg}
                                            </FormControl.Feedback>
                                          )}
                                        </ErrorMessage>
                                      </Form.Group>
                                      <Form.Group
                                        className="mb-lg-3 mb-1"
                                        controlId={`news_item_links.${index}.target`}
                                      >
                                        <FormLabel>Target</FormLabel>
                                        <Field
                                          name={`news_item_links.${index}.target`}
                                          type="text"
                                          as={Form.Control}
                                          isInvalid={
                                            meta.touched?.target &&
                                            !!(typeof meta.error === 'string'
                                              ? meta.error
                                              : meta.error.target)
                                          }
                                        />
                                        <ErrorMessage name={`news_item_links.${index}.target`}>
                                          {(msg) => (
                                            <FormControl.Feedback type="invalid">
                                              {msg}
                                            </FormControl.Feedback>
                                          )}
                                        </ErrorMessage>
                                      </Form.Group>
                                      <Form.Group
                                        className="mb-lg-3 mb-1"
                                        controlId={`news_item_links.${index}.link_type`}
                                      >
                                        <FormLabel>Link Type</FormLabel>
                                        <Field
                                          name={`news_item_links.${index}.link_type`}
                                          type="text"
                                          as={Form.Select}
                                          isInvalid={
                                            meta.touched?.link_type &&
                                            !!(typeof meta.error === 'string'
                                              ? meta.error
                                              : meta.error.link_type)
                                          }
                                        >
                                          <option value="">Select</option>
                                          <option value="article">Article</option>
                                          <option value="video">Video</option>
                                        </Field>
                                        <ErrorMessage name={`news_item_links.${index}.link_type`}>
                                          {(msg) => (
                                            <FormControl.Feedback type="invalid">
                                              {msg}
                                            </FormControl.Feedback>
                                          )}
                                        </ErrorMessage>
                                      </Form.Group>
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="btn-box-shadow text-uppercase border-0"
                                        style={{
                                          width: 84,
                                          background: '#B81F69',
                                          fontSize: '.75rem'
                                        }}
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        Remove
                                      </Button>
                                    </Col>
                                  </Row>
                                </Col>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })
                    ) : (
                      <></>
                    )}
                  </Row>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-link fw-bolder text-uppercase link-primary link-underline-opacity-0"
                  onClick={() =>
                    arrayHelpers.insert(values?.news_item_links.length ?? 0, {
                      text: '',
                      target: '',
                      link_type: ''
                    })
                  }
                >
                  + Add New Link
                </button>
              </Form.Group>
            )}
          />
        </Col>
      </Row>
    </>
  );
};
